<template>
  <div style="max-width: 1000px">
    <div v-if="resourceSchema">
      <div v-if="!loading">
        <fob-entity-header
          class="fob-entity-header"
          :type="resourceSchema.className"
          :title="resourceData._stringRep"
          :createUser="resourceData._createUser"
          :createDate="resourceData._createDate"
          :updateUser="resourceData._updateUser"
          :updateDate="resourceData._updateDate"
        ></fob-entity-header>

        <fob-entity-details
          class="fob-entity-details"
          :resourceData="resourceData"
          :resourceSchema="resourceSchema"
          :resourceId="resourceId"
        ></fob-entity-details>
        <b-field class="fob-hide-in-print" grouped style="margin-top: 40px" >
          <router-link v-if="userCanEdit" class="control" :to="{
            name: 'editieren',
            params: {
              resourceType: resourceData._entityName.split('.').splice(-1)[0].toLowerCase(),
              resourceId: resourceId
            }
          }">
            <button class="control button is-primary">
              <b-icon pack="fa" icon="edit"></b-icon>
              <translate>details.button.edit</translate>
            </button>
          </router-link>
          <button @click="print" class="control button is-primary">
            <b-icon pack="fa" icon="print"></b-icon>
            <translate>details.button.print</translate>
          </button>
          <b-tooltip
            label="Needs proper deduplication"
            position="is-right"
            type="is-danger"
            :active="needsDeduplication"
          >
            <button
              class="control button is-danger"
              v-if="userCanEdit && userIsAtLeastManager"
              :disabled="needsDeduplication"
              @click="deleteEntity()"
            >
              <b-icon pack="fa" icon="trash"></b-icon>
              <translate>details.button.delete</translate>
            </button>
            </b-tooltip>
        </b-field>
      </div>
    </div>
    <div class="section" style="max-width: 1200px" v-else>
      <div class="columns">
        <div class="column" style="max-width: 50px;">
          <b-icon icon="exclamation" type="is-aquamarine" size="is-large"></b-icon>
        </div>
        <div class="column">
          Hier sehen Sie alle Details zu einer einzelnen Ressource / Forschungsaktivität. Im Moment haben Sie noch
          <strong>keine</strong> Ressource zur Anzeige ausgewählt. Nutzen Sie dafür
          die <router-link to="auflisten">Listenansicht</router-link>.
        </div>
      </div>
    </div>
    <div class="print-footer">
      <span>
        <translate :translate-params="{printDate: printDate}">details.printedOn</translate>
      </span>
    </div>
  </div>
</template>

<script>
import EntityDetails from '@/components/EntityDetails'
import EntityHeader from '@/components/EntityHeader'
import { mapGetters } from 'vuex'
import selection from '@/schema/Selection'
import schema from '@/schema'
import { ToastError, ToastSuccess, currentDateString } from '@/utils'
import DynamicTitle from '@/components/mixins/DynamicTitle'

export default {
  name: 'fob-entity',
  mixins: [DynamicTitle],
  data () {
    return {
      resourceTypes: schema.baseTypes,
      printDate: currentDateString(),
      leurisHead: {
        inner: 'leuris:Forschungsbericht',
        complement: 'menu.details'
      }
    }
  },
  mounted () {
    if (this.viewEntityType !== null) {
      this.$router.push({
        name: 'anzeigen_einzeln',
        params: {
          resourceType: this.$store.getters['resource/getViewEntityType'],
          resourceId: this.$store.getters['resource/getId']
        }
      })
    }

    setInterval(() => {
      this.printDate = currentDateString()
    }, 30 * 1000)
  },
  components: {
    'fob-entity-details': EntityDetails,
    'fob-entity-header': EntityHeader
  },
  computed: {
    ...mapGetters({
      resourceSchema: 'resource/getSchema',
      resourceData: 'resource/getData',
      resourceId: 'resource/getId',
      viewEntityType: 'resource/getViewEntityType',
      loading: 'resource/isLoading'
    }),
    userIsAtLeastManager () {
      let roles = this.$store.getters['user/roles']
      return roles['ADMIN'] === true || roles['MANAGER'] === true
    },
    userCanEdit () {
      return selection(this.$store.getters['user/getUser'])
        .map(x => x.entities)
        .reduce((x, y) => x.concat(y), [])
        .map(x => x.className)
        .includes(this.resourceSchema.className) && this.userCanEditByKostenstelle(this.resourceData)
    },
    needsDeduplication () {
      return ['person', 'forschungsprojekt'].includes(this.resourceSchema.className)
    },
    selectedType: {
      get () {
        const index = this.resourceTypes.findIndex(x => x.schema.route === this.viewEntityType)
        if (index !== -1) {
          return index
        }
        return null
      },
      set (newType) {
        this.$router.push({
          name: 'anzeigen_einzeln',
          params: {
            resourceType: this.resourceTypes[newType].schema.route,
            resourceId: this.resourceId
          }
        })
      }
    },
    id: {
      get () {
        return this.$store.getters['resource/getId']
      },
      set (value) {
        this.$router.push({
          name: 'anzeigen_einzeln',
          params: {
            resourceType: this.resourceSchema.route,
            resourceId: value
          }
        })
        // this.$store.dispatch('resource/setResource', { id: value })
        // .then(() => {
        // })
      }
    }
  },
  methods: {
    deleteEntity () {
      this.$dialog.confirm({
        title: 'Datensatz löschen',
        message: `Sind Sie wirklich sicher, dass Sie den Datensatz <strong>"${this.resourceData._stringRep}"</strong> löschen wollen? Diese Aktion kann <i>nicht</i> rückgängig gemacht werden!`,
        cancelText: 'Abbrechen',
        confirmText: 'Ja, jetzt löschen!',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.$store.dispatch('resource/doDelete')
            .then(() => {
              this.$router.push({
                name: 'anzeigen'
              })
              ToastSuccess(this.$gettext(`toast.delete.record`))
            })
            .catch(() => {
              this.$store.dispatch('resetLoading')
              ToastError(this.$gettext(`toast.delete.record.error`))
            })
        }
      })
    },
    editEntity () {
      this.$router.push({
        name: 'editieren_einzeln',
        params: {
          resourceType: this.resourceTypes[this.selectedType].schema.route,
          resourceId: this.resourceId
        }
      })
    },
    userCanEditByKostenstelle (data) {
      let userKst = this.$store.getters['user/selectedOrga'].kostenstelle
      if (data.organisationseinheit != null) {
        let kostenstellen = data.organisationseinheit.kostenstellenpfad
        if (data.weitereOrganisationseinheiten != null) {
          kostenstellen += data.weitereOrganisationseinheiten.map(x => x.kostenstellenpfad).join(' ')
        }
        return kostenstellen.includes(userKst) || (this.$store.getters['user/roles'].ADMIN === true)
      } else {
        return true
      }
    },
    print () {
      window.print()
    }
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/Entity.vue